<script setup lang="ts">
import { DatabaseZap, KeyRound, PencilLine, Server, Trash2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { deleteDatasource, rotateSshKey, testDatasource, updateDatasource, useDatasources, useDatasourceTypes, type DatasourceInput } from "@/api/admin";
import type { Job, ServerImportJobResult } from "@/api/contracts";
import DatasourceForm from "@/components/admin/DatasourceForm.vue";
import DatasourceRename from "@/components/admin/DatasourceRename.vue";
import ForgeServerImport from "@/components/admin/ForgeServerImport.vue";
import ForgeDiscover from "@/components/admin/setup/ForgeDiscover.vue";
import JobProgress from "@/components/jobs/JobProgress.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import type { TestResult } from "@/components/admin/setup/ConnectionTestResult.vue";
import { errorMessage } from "@/lib/errors";

definePage({ meta: { title: "Datasource", requires: { adminArea: "datasources" } } });

const route = useRoute("/admin/datasources/[type]/[name]");
const router = useRouter();
const queryClient = useQueryClient();
const types = useDatasourceTypes();
const datasources = useDatasources();
const ds = computed(() => datasources.data.value?.find((d) => d.type === route.params.type && d.name === route.params.name));
const lineages = computed(() => [...new Set((datasources.data.value ?? []).map((d) => d.labels.lineage).filter(Boolean) as string[])].sort());
const busy = ref(false);
const error = ref<unknown>(null);
const testing = ref(false);
const testResult = ref<TestResult | null>(null);
const formKey = ref(0);
const importOpen = ref(false);
const discoverOpen = ref(false);
const renameOpen = ref(false);
const rotateJob = ref<string | null>(null);
const rotating = ref(false);
const rotateResult = ref<ServerImportJobResult | null>(null);
const forgeLinked = computed(() => ds.value?.type === "ssh" && typeof ds.value.config.forgeServerId === "number");

async function save(value: DatasourceInput) {
  if (!ds.value) return;
  busy.value = true;
  error.value = null;
  try {
    await updateDatasource(ds.value.type, ds.value.name, { description: value.description, labels: value.labels, config: value.config, enabled: value.enabled });
    await queryClient.invalidateQueries({ queryKey: ["admin", "datasources"] });
    toast.success("Saved.");
    testResult.value = null;
    formKey.value++;
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

async function test() {
  if (!ds.value) return;
  testing.value = true;
  try {
    testResult.value = await testDatasource(ds.value.type, ds.value.name);
  } catch (err) {
    testResult.value = { ok: false, message: errorMessage(err) };
  } finally {
    testing.value = false;
  }
}

async function rotate() {
  if (!ds.value) return;
  const prod = ds.value.labels.environment === "prod";
  const answer = await confirm({
    title: `Rotate the key of ${ds.value.name}?`,
    description: t.admin.rotateKeyConfirm,
    confirmLabel: t.admin.rotateKey,
    ...(prod ? { typeToConfirm: ds.value.name } : {}),
  });
  if (!answer) return;
  try {
    rotateResult.value = null;
    rotateJob.value = (await rotateSshKey(ds.value.name)).jobId;
    rotating.value = true;
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function rotated(job: Job) {
  rotating.value = false;
  rotateResult.value = (job.result as ServerImportJobResult | null) ?? null;
  await queryClient.invalidateQueries({ queryKey: ["admin", "datasources"] });
}

async function renamed(name: string) {
  const type = ds.value?.type ?? String(route.params.type);
  await queryClient.invalidateQueries({ queryKey: ["admin", "datasources"] });
  toast.success(`Renamed to ${name}.`);
  await router.replace(`/admin/datasources/${type}/${name}`);
}

async function remove() {
  if (!ds.value) return;
  const answer = await confirm({
    title: `Delete ${ds.value.name}?`,
    description: "Tools lose access to it immediately. Grants that name it stay but match nothing.",
    confirmLabel: t.common.delete,
    destructive: true,
    typeToConfirm: ds.value.name,
  });
  if (!answer) return;
  try {
    await deleteDatasource(ds.value.type, ds.value.name);
    await queryClient.invalidateQueries({ queryKey: ["admin", "datasources"] });
    toast.success("Deleted.");
    await router.push("/admin/datasources");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="datasources.isPending.value || types.isPending.value" :rows="6" />
    <ErrorState v-else-if="datasources.error.value || types.error.value" :error="datasources.error.value ?? types.error.value" @retry="datasources.refetch()" />
    <EmptyState v-else-if="!ds" title="Datasource not found" :description="`${route.params.type}/${route.params.name} doesn't exist (any more).`">
      <Button as-child variant="outline"><RouterLink to="/admin/datasources">{{ t.admin.datasources }}</RouterLink></Button>
    </EmptyState>
    <template v-else>
      <PageHeader :title="ds.name" :description="ds.description" :crumbs="[{ label: t.admin.title, to: '/admin' }, { label: t.admin.datasources, to: '/admin/datasources' }, { label: `${ds.type}/${ds.name}` }]">
        <template #badges>
          <Badge variant="muted">{{ types.data.value?.find((x) => x.type === ds!.type)?.label ?? ds.type }}</Badge>
          <Badge v-if="ds.target" variant="accent" class="font-mono">{{ ds.target }}</Badge>
          <Badge v-if="!ds.enabled" variant="warning">{{ t.common.disabled }}</Badge>
        </template>
        <template #meta>
          <p class="mt-1 text-xs text-muted-foreground">Last changed <RelativeTime :value="ds.updatedAt" /><template v-if="ds.updatedBy"> by {{ ds.updatedBy }}</template></p>
        </template>
        <template #actions>
          <Button v-if="ds.type === 'forge'" variant="outline" @click="importOpen = true"><Server /> {{ t.admin.importServers }}</Button>
          <Button v-if="ds.type === 'forge'" variant="outline" @click="discoverOpen = true"><DatabaseZap /> Discover databases</Button>
          <Button v-if="forgeLinked" variant="outline" :disabled="rotating" @click="rotate"><KeyRound /> {{ t.admin.rotateKey }}</Button>
          <Button variant="outline" @click="renameOpen = true"><PencilLine /> Rename</Button>
          <Button variant="ghost" class="text-destructive" @click="remove"><Trash2 /> {{ t.common.delete }}</Button>
        </template>
      </PageHeader>
      <div v-if="rotateJob" class="mb-6 flex flex-col gap-2">
        <JobProgress :job-id="rotateJob" @finished="rotated" />
        <template v-for="r in rotateResult?.results ?? []" :key="r.datasource ?? r.serverId">
          <p class="text-sm" :class="r.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'">{{ r.message }}</p>
          <p v-for="w in r.warnings" :key="w" class="text-xs text-muted-foreground">{{ w }}</p>
        </template>
      </div>
      <DatasourceRename v-model:open="renameOpen" :type="ds.type" :name="ds.name" @renamed="renamed" />
      <ForgeServerImport v-if="ds.type === 'forge'" v-model:open="importOpen" :forge="ds.name" />
      <ForgeDiscover v-if="ds.type === 'forge'" v-model:open="discoverOpen" :forge="ds.name" :lineages="lineages" />
      <DatasourceForm
        :key="`${ds.updatedAt}-${formKey}`"
        :types="types.data.value ?? []"
        :datasource="ds"
        :lineages="lineages"
        :busy="busy"
        :error="error"
        :test-result="testResult"
        :testing="testing"
        @submit="save"
        @test="test"
        @cancel="router.push('/admin/datasources')"
      />
    </template>
  </div>
</template>
